import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DEV_MODE, fetchFilings, fetchPrices } from '../lib/api';
import { pushRecentTicker, readLookupCache, readRecentTickers, writeLookupCache } from '../lib/cache';
import {
  MICRO_CAP_THRESHOLD,
  REFERENCE_LEVELS,
  belowThreshold,
  buildObservations,
  buildSeries,
  summarize,
  unaccountedSplits,
} from '../lib/marketCap';
import { formatDate, formatMarketCap, formatPercent, formatShares, isoLocalDate } from '../lib/format';
import AskClaude from './AskClaude';
import BelowThreshold from './BelowThreshold';
import FilingsTable from './FilingsTable';
import ToolsNav from './ToolsNav';
import MarketCapChart from './MarketCapChart';
import MarketCapTable from './MarketCapTable';
import UserMenu from './UserMenu';

const DEFAULT_YEARS = 7;
const SHARES_COLOR = '#1e5aa8'; // matches --series-shares

function isoMonthsAgo(months) {
  const d = new Date();
  d.setMonth(d.getMonth() - months);
  return d.toISOString().slice(0, 10);
}

export default function Dashboard({ user }) {
  const [tickerInput, setTickerInput] = useState('');
  const [yearsInput, setYearsInput] = useState(String(DEFAULT_YEARS));
  const [recents, setRecents] = useState(readRecentTickers);

  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState('');
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const [showShares, setShowShares] = useState(false);
  const [showFilingMarks, setShowFilingMarks] = useState(true);

  const runLookup = useCallback(async (rawTicker, rawYears, { force = false } = {}) => {
    const ticker = String(rawTicker).trim().toUpperCase();
    const years = Math.min(20, Math.max(1, Math.round(Number(rawYears) || DEFAULT_YEARS)));

    if (!/^[A-Z][A-Z0-9.\-]{0,9}$/.test(ticker)) {
      setError('Enter a valid ticker symbol.');
      return;
    }

    setLoading(true);
    setError(null);
    setData(null);

    try {
      const windowStart = isoMonthsAgo(years * 12);

      // A whole lookup — filings *and* prices — is cached together, so a repeat visit
      // costs one Firestore read rather than several SEC round trips per filing
      // against a rate limit shared by everyone using the tool. `force` is the
      // Refresh button: it skips the read and rebuilds the entry from source.
      setProgress(force ? 'Rebuilding from source…' : 'Checking cache…');
      const cached = force ? null : await readLookupCache(ticker, years);

      let { filings, prices } = cached ?? {};
      if (!cached) {
        setProgress('Reading 10-K and 10-Q filings from SEC EDGAR…');
        filings = await fetchFilings(ticker, years);

        setProgress('Fetching daily prices…');
        prices = await fetchPrices(ticker, windowStart);

        await writeLookupCache(ticker, years, { filings, prices });
      }

      setData({
        ticker,
        years,
        windowStart,
        filings,
        prices,
        // Null when this lookup was built live. Drives the "as of" stamp, which is
        // what makes the staleness the cache buys visible rather than silent.
        cachedAtMs: cached?.cachedAtMs ?? null,
      });
      setRecents(pushRecentTicker(ticker));
    } catch (err) {
      setError(err.message ?? 'Something went wrong.');
    } finally {
      setLoading(false);
      setProgress('');
    }
  }, []);

  function onSubmit(event) {
    event.preventDefault();
    if (!loading) runLookup(tickerInput, yearsInput);
  }

  // Deep link: `/?ticker=XYZ` (optionally `&years=N`) fills the box and runs the
  // lookup. Used by the Priorities board to jump from a deal card straight to that
  // deal's history. The param is stripped afterwards so a refresh doesn't re-run it,
  // and `?dev` is preserved because dropping it would bounce a dev session to login.
  const deepLinkHandled = useRef(false);
  useEffect(() => {
    if (deepLinkHandled.current) return;
    deepLinkHandled.current = true;

    const params = new URLSearchParams(window.location.search);
    const ticker = (params.get('ticker') ?? '').trim().toUpperCase();
    if (!/^[A-Z][A-Z0-9.\-]{0,9}$/.test(ticker)) return;

    const yearsParam = params.get('years');
    const years = yearsParam && Number(yearsParam) ? String(yearsParam) : String(DEFAULT_YEARS);

    setTickerInput(ticker);
    setYearsInput(years);
    window.history.replaceState(null, '', DEV_MODE ? '/?dev' : '/');
    runLookup(ticker, years);
  }, [runLookup]);

  // --- Derived views -------------------------------------------------------

  const observations = useMemo(
    () => (data ? buildObservations(data.filings.filings, data.prices.splits) : []),
    [data],
  );

  const series = useMemo(
    () => (data ? buildSeries(data.prices.bars, observations, data.windowStart) : []),
    [data, observations],
  );

  const stats = useMemo(() => summarize(series), [series]);

  const threshold = useMemo(() => belowThreshold(series, MICRO_CAP_THRESHOLD), [series]);

  /** Filing rows for the table: every filing, enriched with its split-adjusted figures. */
  const filingRows = useMemo(() => {
    if (!data) return [];
    const byAccession = new Map(observations.map((o) => [o.accessionNumber, o]));
    return data.filings.filings.map((f) => {
      const obs = byAccession.get(f.accessionNumber);
      return {
        ...f,
        splitFactor: obs?.splitFactor ?? null,
        adjustedShares: obs?.adjustedShares ?? null,
        // A filing that produced a number but isn't in `observations` was superseded
        // by a later filing reporting the same as-of date.
        superseded: Boolean(f.shares) && !obs,
      };
    });
  }, [data, observations]);

  const warnings = useMemo(() => {
    if (!data) return [];
    const list = [...(data.filings.warnings ?? [])];

    // Only a genuine coverage gap is worth flagging. Comparing the first *bar* to the
    // window start instead would fire whenever the window opens on a weekend.
    const firstObs = observations[0];
    if (firstObs && firstObs.asOfDate > data.windowStart) {
      list.push(
        `No filing covers ${formatDate(data.windowStart)} to ${formatDate(firstObs.asOfDate)}. ` +
          'The chart starts at the earliest date with a share count we actually have, rather than ' +
          'extending the oldest count backwards.',
      );
    }

    // Splits are load-bearing: without them the price and share bases disagree and
    // every market cap before a split date is wrong by the split ratio. A failed
    // lookup returns an empty list, which is indistinguishable from "never split" —
    // so the failure is stated outright rather than left to look like clean data.
    if (data.prices.splitsAvailable === false) {
      list.push(
        `Split history could not be loaded from Massive${data.prices.splitsError ? ` (${data.prices.splitsError})` : ''}. ` +
          'Filed share counts have therefore not been adjusted onto the price basis. If ' +
          `${data.ticker} split during this window, every market cap before the split date is ` +
          'wrong by the split ratio. Figures for a company that never split are unaffected.',
      );
    }

    // Independent of whether the lookup succeeded: a correctly adjusted series is
    // continuous across a split, so a clean whole-ratio jump that survives adjustment
    // means a split is missing or wrong in the data we were given.
    for (const gap of unaccountedSplits(observations)) {
      list.push(
        `Shares outstanding jump ${gap.ratio.toFixed(2)}× between ${formatDate(gap.from.asOfDate)} and ` +
          `${formatDate(gap.to.asOfDate)}, after split adjustment — the shape of an unrecorded ` +
          `${gap.label} split. If that is what it is, market caps before ${formatDate(gap.to.asOfDate)} ` +
          'are wrong by that factor. If the company genuinely issued or retired that many shares, ' +
          'the figures are correct.',
      );
    }

    // Cross-check against the provider's own number. That figure is exactly what this
    // tool exists to avoid depending on, so a disagreement is reported, never resolved.
    const reference = data.prices.referenceSharesOutstanding;
    const latest = observations[observations.length - 1];
    if (reference && latest) {
      // Compare like with like: the vendor counts the security its price is for, which
      // for an ADR is receipts, not ordinary shares. Checking against the ordinary count
      // would make an eightfold disagreement look like a mild one.
      const ours = latest.tradableShares;
      const gap = Math.abs(reference - ours) / ours;
      if (gap > 0.03) {
        list.push(
          `Massive reports ${formatShares(reference)} ${latest.adsRatio ? 'ADSs' : 'shares'} outstanding ` +
            `for ${data.ticker}; the latest filing gives ${formatShares(ours)}` +
            `${latest.adsRatio ? ` (${formatShares(latest.shares * latest.splitFactor)} ordinary ÷ ${latest.adsRatio})` : ''} — ` +
            `${(gap * 100).toFixed(1)}% apart. The filing figure is what this tool uses.`,
        );
      }
    }
    return list;
  }, [data, observations, series]);

  /**
   * Multi-class issuers need saying out loud. This tool multiplies price by the traded
   * class only, so for Alphabet the headline is Class A — roughly half of what anyone
   * else calls "Alphabet's market cap". Showing the all-class figure alongside it is
   * the difference between a defensible number and a misleading one.
   */
  const classNotice = useMemo(() => {
    const latest = observations[observations.length - 1];
    if (!latest?.classes || latest.classes.length < 2 || !latest.totalAllClasses) return null;
    const allClassesCap =
      ((latest.totalAllClasses * latest.splitFactor) / (latest.adsRatio ?? 1)) *
      (series[series.length - 1]?.close ?? 0);
    return {
      className: latest.shareClass ?? 'the traded class',
      classShares: latest.shares,
      totalShares: latest.totalAllClasses,
      allClassesCap,
      breakdown: latest.classes,
    };
  }, [observations, series]);

  /**
   * An ADR's price is per receipt and its filings count ordinary shares. Everything on
   * screen is on the receipt's basis, which is the only one that matches the price — but
   * that has to be said, because the filings table below shows the ordinary counts and a
   * reader comparing the two would otherwise find them a clean integer apart.
   */
  const adsNotice = useMemo(() => {
    const latest = observations[observations.length - 1];
    if (!latest?.adsRatio) return null;
    return {
      ratio: latest.adsRatio,
      security: latest.tradedSecurity,
      ordinary: latest.adjustedShares,
      tradable: latest.tradableShares,
    };
  }, [observations]);

  /**
   * Everything the Ask Claude panel needs, in one object. Assembled from the
   * memos above rather than from `data`, so the panel describes the figures on
   * screen instead of re-deriving them — the split adjustment and the ADS ratio
   * are exactly the places a second derivation would quietly disagree.
   */
  const lookup = useMemo(
    () => ({
      ticker: data?.ticker ?? null,
      years: data?.years ?? null,
      windowStart: data?.windowStart ?? null,
      cachedAtMs: data?.cachedAtMs ?? null,
      stats,
      threshold,
      filingRows,
      warnings,
      classNotice,
      adsNotice,
      series,
    }),
    [data, stats, threshold, filingRows, warnings, classNotice, adsNotice, series],
  );

  return (
    <div className="ask-app">
      <AskClaude lookup={lookup} />
      <header className="app-header">
        {/* This tool's own home, not the Jules Tools index. A header mark is how you get
            back to the start of the thing you are in; the route out to the other tools
            is the "All Jules Tools" link inside the nav dropdown, which is where someone
            leaving actually looks. `?dev` is carried through or the reset would land on
            the login screen in development. */}
        <a className="app-brand" href={DEV_MODE ? '/?dev' : '/'}>
          <img className="app-logo" src="/v_transparent.png" alt="Chicago Venture Partners" />
          <div>
            <h1 className="app-title">Historical Market Cap</h1>
            <p className="app-subtitle">Chicago Venture Partners</p>
          </div>
        </a>
        <div className="app-header-spacer" />
        <ToolsNav />
        {DEV_MODE ? <span className="hint">dev mode</span> : <UserMenu user={user} />}
      </header>

      <main className="page">
        <section className="card">
          <div className="card-body">
            <form className="controls" onSubmit={onSubmit}>
              <div className="field">
                <label className="field-label" htmlFor="ticker">Ticker</label>
                <input
                  id="ticker"
                  className="input input-ticker"
                  value={tickerInput}
                  onChange={(e) => setTickerInput(e.target.value)}
                  placeholder="AAPL"
                  autoComplete="off"
                  spellCheck={false}
                />
              </div>
              <div className="field">
                <label className="field-label" htmlFor="years">Years of history</label>
                <input
                  id="years"
                  className="input input-num"
                  type="number"
                  min="1"
                  max="20"
                  step="1"
                  style={{ minWidth: 110 }}
                  value={yearsInput}
                  onChange={(e) => setYearsInput(e.target.value)}
                />
              </div>
              <button type="submit" className="btn" disabled={loading}>
                {loading ? (
                  <>
                    <span className="spinner" /> Working…
                  </>
                ) : (
                  'Build market cap'
                )}
              </button>
              {progress && <span className="hint">{progress}</span>}
            </form>

            {recents.length > 0 && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 14, alignItems: 'center' }}>
                <span className="hint">Recent:</span>
                {recents.map((t) => (
                  <button
                    key={t}
                    type="button"
                    className="chip"
                    onClick={() => {
                      setTickerInput(t);
                      runLookup(t, yearsInput);
                    }}
                  >
                    {t}
                  </button>
                ))}
              </div>
            )}

            {/* What the cache costs, stated rather than hidden. Within the TTL a
                lookup is served whole from Firestore, so both the last price and the
                filing list are as of the day the entry was built — including a 10-Q
                filed since. Refresh is the way out of both. */}
            {data && !loading && (
              <div className="cache-stamp">
                <span className="hint">
                  {data.cachedAtMs
                    ? `Filings and prices as of ${formatDate(isoLocalDate(data.cachedAtMs))} (cached).`
                    : 'Built just now from SEC EDGAR and Massive.'}
                </span>
                <button
                  type="button"
                  className="chip"
                  onClick={() => runLookup(data.ticker, data.years, { force: true })}
                >
                  Refresh
                </button>
              </div>
            )}
          </div>
        </section>

        <div className="notice notice-info">
          <span className="notice-mark" aria-hidden="true">›</span>
          <span>
            <strong>This version reads 10-K, 10-Q, 20-F and 40-F filings</strong> (plus their
            amendments). Share counts come from the XBRL cover page of each filing and are dated the
            way the filing dates them. Foreign issuers whose US listing is an{' '}
            <strong>American Depositary Share</strong> are read only when the cover states how many
            ordinary shares one ADS represents; where it doesn't, the filings are shown but no
            market cap is, because the price and the share count would be an unknown factor apart.
            Counts from 8-K, S-1 and 424B filings are not read yet.
          </span>
        </div>

        {error && (
          <div className="notice notice-error">
            <span className="notice-mark" aria-hidden="true">!</span>
            <span>{error}</span>
          </div>
        )}

        {classNotice && (
          <div className="notice notice-warn">
            <span className="notice-mark" aria-hidden="true">!</span>
            <span>
              <strong>{data.ticker} has more than one share class.</strong> Every figure below uses{' '}
              <strong>{classNotice.className}</strong> only — {formatShares(classNotice.classShares)} shares —
              because that is the class the {data.ticker} price applies to. All classes together total{' '}
              {formatShares(classNotice.totalShares)} shares (
              {classNotice.breakdown.map((c) => `${c.label}: ${formatShares(c.shares)}`).join(', ')}
              ), which at the latest close would be {formatMarketCap(classNotice.allClassesCap)}.
            </span>
          </div>
        )}

        {adsNotice && (
          <div className="notice notice-warn">
            <span className="notice-mark" aria-hidden="true">!</span>
            <span>
              <strong>{data.ticker} trades as an American Depositary Share.</strong> The cover page
              states it as “{adsNotice.security}”, so one {data.ticker} share is a claim on{' '}
              {adsNotice.ratio} ordinary shares. Every figure above is on that basis —{' '}
              {formatShares(adsNotice.ordinary)} ordinary shares ÷ {adsNotice.ratio} ={' '}
              {formatShares(adsNotice.tradable)} ADS-equivalents — because the price is quoted per
              ADS. The filings table below reports the ordinary counts as filed, which is why the
              two differ by exactly {adsNotice.ratio}×. Total market cap is the same either way.
            </span>
          </div>
        )}

        {warnings.map((warning) => (
          <div className="notice notice-warn" key={warning}>
            <span className="notice-mark" aria-hidden="true">!</span>
            <span>{warning}</span>
          </div>
        ))}

        {data && (
          <>
            <section className="card">
              <div className="card-head">
                <h2 className="card-title">
                  {data.filings.companyName} <span className="hint">({data.ticker})</span>
                </h2>
                <p className="card-note">
                  CIK {data.filings.cik} ·{' '}
                  <a
                    href={`https://www.sec.gov/cgi-bin/browse-edgar?action=getcompany&CIK=${data.filings.cik}&type=10-&dateb=&owner=include&count=40`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    EDGAR filings
                  </a>
                  {data.filings.fromCache && ' · filings served from cache'}
                </p>
              </div>

              {stats ? (
                <div className="stats">
                  <div className="stat">
                    <p className="stat-label">Market cap</p>
                    <p className="stat-value stat-hero">{formatMarketCap(stats.last.marketCap)}</p>
                    <p className="stat-meta">{formatDate(stats.last.date)} close</p>
                  </div>
                  <div className="stat">
                    <p className="stat-label">Change over {data.years}y</p>
                    <p className={`stat-value ${stats.changePct >= 0 ? 'delta-up' : 'delta-down'}`}>
                      {formatPercent(stats.changePct)}
                    </p>
                    <p className="stat-meta">from {formatMarketCap(stats.first.marketCap)}</p>
                  </div>
                  <div className="stat">
                    <p className="stat-label">Period high</p>
                    <p className="stat-value">{formatMarketCap(stats.high.marketCap)}</p>
                    <p className="stat-meta">{formatDate(stats.high.date)}</p>
                  </div>
                  <div className="stat">
                    <p className="stat-label">Period low</p>
                    <p className="stat-value">{formatMarketCap(stats.low.marketCap)}</p>
                    <p className="stat-meta">{formatDate(stats.low.date)}</p>
                  </div>
                  <div className="stat">
                    <p className="stat-label">
                      {adsNotice
                        ? 'ADS equivalents'
                        : classNotice
                          ? `Shares — ${classNotice.className}`
                          : 'Shares outstanding'}
                    </p>
                    <p className="stat-value">{formatShares(stats.last.shares)}</p>
                    <p className="stat-meta">
                      {stats.last.form} · as of {formatDate(stats.last.asOfDate)}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="empty">
                  No market cap could be computed — no filing in range produced a usable share count.
                </div>
              )}
            </section>

            {series.length > 0 && (
              <section className="card">
                <div className="card-head">
                  <h2 className="card-title">Market cap, {data.years}-year history</h2>
                  <p className="card-note">
                    daily close × the {classNotice ? `${classNotice.className} ` : ''}share count in
                    effect that day
                  </p>
                  <div className="card-actions">
                    <label className="toggle">
                      <input
                        type="checkbox"
                        checked={showShares}
                        onChange={(e) => setShowShares(e.target.checked)}
                      />
                      <span className="toggle-key" style={{ background: SHARES_COLOR }} />
                      Shares outstanding
                    </label>
                    <label className="toggle">
                      <input
                        type="checkbox"
                        checked={showFilingMarks}
                        onChange={(e) => setShowFilingMarks(e.target.checked)}
                      />
                      Mark filing dates
                    </label>
                  </div>
                </div>
                <div className="card-body">
                  <MarketCapChart
                    series={series}
                    observations={observations}
                    showShares={showShares}
                    showFilingMarks={showFilingMarks}
                    threshold={MICRO_CAP_THRESHOLD}
                    referenceLevels={REFERENCE_LEVELS}
                  />
                </div>
              </section>
            )}

            {series.length > 0 && (
              <BelowThreshold ticker={data.ticker} summary={threshold} years={data.years} />
            )}

            <FilingsTable ticker={data.ticker} rows={filingRows} windowStart={data.windowStart} />

            {series.length > 0 && <MarketCapTable ticker={data.ticker} series={series} />}
          </>
        )}

        {!data && !loading && !error && (
          <div className="empty">
            Enter a ticker to pull its share counts from SEC filings and build a market cap history.
          </div>
        )}
      </main>
    </div>
  );
}
